import { useSnackbar } from '@app/hooks/useSnackbar';
import {
  CustomTraining,
  useCreateCustomTrainingMutation,
  useUpdateCustomTrainingMutation,
} from '@dieterApi/customTraining/useCustomTrainings';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

const MAX_FILE_SIZE = 10_000_000;
const ACCEPT =
  'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png';

// '0' = einmalig (renewalMonths null)
const RENEWAL_OPTIONS = ['0', '6', '12', '24'] as const;

interface Props {
  open: boolean;
  // undefined = neue Schulung anlegen
  training?: CustomTraining;
  onClose: () => void;
}

export function CustomTrainingModal({ open, training, onClose }: Props) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [createTraining, { loading: creating }] = useCreateCustomTrainingMutation();
  const [updateTraining, { loading: updating }] = useUpdateCustomTrainingMutation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [renewal, setRenewal] = useState<string>('0');
  const [file, setFile] = useState<File | null>(null);
  const saving = creating || updating;
  const isEdit = !!training;

  useEffect(() => {
    if (!open) return;
    setTitle(training?.title ?? '');
    setDescription(training?.description ?? '');
    setRenewal(training?.renewalMonths ? String(training.renewalMonths) : '0');
    setFile(null);
  }, [open, training]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: ACCEPT,
    multiple: false,
    onDrop: (accepted, rejected) => {
      if (rejected.length > 0 || accepted.length === 0) {
        enqueueSnackbar(
          t(
            'dashboard.training.custom.modal.invalid_file',
            'Bitte lade eine PDF-, Word- (DOC/DOCX), JPG- oder PNG-Datei hoch.'
          ),
          { variant: 'error' }
        );
        return;
      }
      if (accepted[0].size > MAX_FILE_SIZE) {
        enqueueSnackbar(t('components.ui.fileupload.toolarge'), { variant: 'error' });
        return;
      }
      setFile(accepted[0]);
    },
  });

  const handleSave = async () => {
    const renewalMonths = Number(renewal) || null;
    try {
      if (training) {
        await updateTraining({
          variables: {
            id: training.id,
            title: title.trim(),
            description: description.trim() || null,
            renewalMonths,
            ...(file ? { file, size: file.size } : {}),
          },
        });
      } else if (file) {
        await createTraining({
          variables: {
            title: title.trim(),
            description: description.trim() || null,
            type: 'Acknowledgement',
            renewalMonths,
            file,
            size: file.size,
          },
        });
      }
      enqueueSnackbar(t('dashboard.training.custom.snackbars.saved', 'Schulung gespeichert'), { variant: 'success' });
      onClose();
    } catch (e: any) {
      enqueueSnackbar(e.message, { variant: 'error' });
    }
  };

  const canSave = !!title.trim() && (isEdit || !!file) && !saving;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ pt: 3, pb: 1.5, px: 3 }}>
        {isEdit
          ? t('dashboard.training.custom.modal.title_edit', 'Schulung bearbeiten')
          : t('dashboard.training.custom.modal.title_create', 'Eigene Schulung anlegen')}
      </DialogTitle>
      <DialogContent sx={{ px: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <p className="text-sm text-gray-500">
          {t(
            'dashboard.training.custom.modal.intro',
            'Lade ein eigenes Dokument hoch, z. B. eine interne Richtlinie. Deine Mitarbeiter erhalten wie bei den anderen Schulungen einen persönlichen Link, sehen das Dokument an und bestätigen die Kenntnisnahme.'
          )}
        </p>
        <TextField
          label={t('dashboard.training.custom.modal.fields.title', 'Titel')}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          autoFocus
          size="small"
          inputProps={{ maxLength: 200, 'data-testid': 'custom-training-title' }}
          sx={{ mt: 1 }}
        />
        <TextField
          label={t('dashboard.training.custom.modal.fields.description', 'Hinweis für Mitarbeiter (optional)')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          minRows={2}
          size="small"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField
            select
            label={t('dashboard.training.custom.modal.fields.type', 'Art der Schulung')}
            value="Acknowledgement"
            size="small"
            disabled
          >
            <MenuItem value="Acknowledgement">
              {t('dashboard.training.custom.types.Acknowledgement', 'Dokument zur Kenntnisnahme')}
            </MenuItem>
          </TextField>
          <TextField
            select
            label={t('dashboard.training.custom.modal.fields.renewal', 'Erneute Bestätigung')}
            value={renewal}
            onChange={(e) => setRenewal(e.target.value)}
            size="small"
          >
            {RENEWAL_OPTIONS.map((months) => (
              <MenuItem key={months} value={months}>
                {renewalLabel(t, Number(months))}
              </MenuItem>
            ))}
          </TextField>
        </div>

        <div>
          <div
            {...getRootProps()}
            className={`flex items-center gap-3 rounded-lg border-2 border-dashed px-4 py-4 cursor-pointer transition-colors ${
              isDragActive ? 'border-primary-root bg-primary-50' : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <input {...getInputProps()} data-testid="custom-training-file" />
            {file ? (
              <InsertDriveFileOutlinedIcon className="text-primary-root" />
            ) : (
              <UploadFileIcon className="text-gray-400" />
            )}
            <div className="min-w-0 flex-1 text-sm">
              {file ? (
                <span className="font-medium break-all">{file.name}</span>
              ) : isEdit ? (
                <>
                  <span className="text-gray-700">
                    {t('dashboard.training.custom.modal.file_current', 'Aktuelles Dokument:')}{' '}
                    <span className="font-medium break-all">{training?.file?.name}</span>
                  </span>
                  <br />
                  <span className="text-gray-500">
                    {t('dashboard.training.custom.modal.file_replace_hint', 'Neue Datei hierher ziehen, um es zu ersetzen')}
                  </span>
                </>
              ) : (
                <span className="text-gray-600">
                  {t('dashboard.training.custom.modal.file_hint', 'Dokument hierher ziehen oder klicken zum Auswählen')}
                </span>
              )}
              <div className="text-xs text-gray-400 mt-0.5">PDF, DOC, DOCX, JPG, PNG · max. 10 MB</div>
            </div>
          </div>
          {isEdit && file && (training?.confirmations.length ?? 0) > 0 && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              {t(
                'dashboard.training.custom.modal.replace_warning',
                'Wenn du das Dokument ersetzt, müssen alle Mitarbeiter die Kenntnisnahme erneut bestätigen. Bisherige Bestätigungen bleiben zur Dokumentation erhalten.'
              )}
            </Alert>
          )}
        </div>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={saving}>
          {t('components.ui.alert_dialog.actions.cancel', 'Abbrechen')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave}
          startIcon={saving ? <CircularProgress size={14} /> : undefined}
          data-testid="custom-training-save"
        >
          {isEdit
            ? t('dashboard.training.custom.modal.save', 'Speichern')
            : t('dashboard.training.custom.modal.create', 'Schulung anlegen')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function renewalLabel(t: (key: string, fallback: string, opts?: any) => string, months?: number | null) {
  if (!months) return t('dashboard.training.custom.renewal.once', 'Einmalig');
  if (months === 12) return t('dashboard.training.custom.renewal.yearly', 'Jährlich');
  return t('dashboard.training.custom.renewal.months', 'Alle {{count}} Monate', { count: months });
}
